import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import useAuth from 'auth/UseAuth';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { DialogCloseButton } from '../../components/DialogCloseButton';
import {
  CheckByFastQValidation,
  CheckByValidation,
  CreateQualityCheck,
  IdMappingErrorMessageType,
  ParentExhaustion,
  PersistableQualityCheckStatuses,
  QualityCheckStatus,
  SampleTrackingCheckBy,
  SampleTrackingCheckByFastqValidationResultPayload,
  SampleTrackingCheckByValidationResult,
  SampleTrackingIdMapping,
  TransitionType,
} from '../../data/SampleTrackingData';
import { SampleTrackingIdMappingGrid } from './SampleTrackingIdMappingGrid';
import { recordQc } from '../../util/Constants';
import { find, findIndex, keyBy, map, values } from 'lodash';
import { DialogOpenButton } from '../../components/DialogOpenButton';
import { CreateSupplementalFilesModal } from './supplementalFiles/CreateSupplementalFilesModal';
import { CreateSupplementalFilesForQualityCheckGroupId, SupplementalFile } from '../../data/SupplementalFileData';
import { ErrorManagement, LoadingProps, LoadingState } from '../../components/LoadingStateUtil';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import { ErrorIndicator } from '../../components/ErrorIndicator';
import UseMemoTranslation from '../../hooks/UseMemoTranslation';
import useTransitionEnums from '../../components/hooks/UseTransitionEnums';
import { FastQData, SampleTrackingIdMappingConfirmationGrid } from './SampleTrackingIdMappingConfirmationGrid';
import {
  CheckByFailedReason,
  SampleIdResolverApproach,
  TransitionEnum,
  TransitionSampleOutputOnCheckBy,
} from '../../data/ReferenceData';
import { DialogTypography } from '../../components/DialogTypography';
import { CancelButton } from '../../components/CancelButton';
import { PrimaryButton } from '../../components/PrimaryButton';
import {
  CheckByConfiguration,
  DefaultConfiguration,
  DefaultSupportsCheckByIssueConfiguration,
  OutputsNotSupportConfiguration,
  OutputsNotSupportedAndSupportsCheckByIssueConfiguration,
  SequencingConfiguration,
} from './CheckByConfiguration';
import { Dictionary, UseState, UseStateSetter } from '../../util/TypeUtil';
import { FlexBox } from '../../components/FlexBox';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import { GridExportButton } from '../../components/GridExportButton';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { CheckByAvailableSequenceFilesGrid } from './CheckByAvailableSequenceFilesGrid';
import FullscreenOutlinedIcon from '@mui/icons-material/FullscreenOutlined';
import { FlexTableBox } from '../../components/FlexTableBox';
import useCheckByFailedReasons from '../../components/hooks/UseCheckByFailedReasons';

type OnCloseFunction = (hasChanges: boolean) => void;
type WidthSize = 'sm' | 'md' | 'lg' | 'xl';

export interface CreateSampleTrackingIdMappingModalProps {
  researchProjectId: string;
  transitionType: TransitionType;
  configuredTransitionId: string;
  onClose?: (hasChanges: boolean) => void;
}

export const CheckByModal = ({
  researchProjectId,
  transitionType,
  configuredTransitionId,
  onClose = () => {},
}: CreateSampleTrackingIdMappingModalProps) => {
  const { t } = UseMemoTranslation();
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const transitionEnums = useTransitionEnums();
  const isSequencing = transitionType === 'Sequencing';

  const [sampleIdResolverApproach, setSampleIdResolverApproach] = useState<SampleIdResolverApproach>('normal');
  const [transitionEnum, setTransitionEnum] = useState<TransitionEnum>();

  const [sampleOutputOnCheckBy, setSampleOutputOnCheckBy] =
    useState<TransitionSampleOutputOnCheckBy>('supportsOutputSamples');
  const [supportsCheckByFailedReason, setSupportsCheckByFailedReason] = useState<boolean>();

  useEffect(() => {
    if (!transitionEnums || !transitionType) {
      return;
    }

    const transition = find(transitionEnums, i => i.name === transitionType);
    setTransitionEnum(transition);
    setSampleIdResolverApproach(transition?.sampleIdResolverApproach ?? 'normal');
    setSampleOutputOnCheckBy(transition?.sampleOutputOnCheckBy || 'supportsOutputSamples');
    setSupportsCheckByFailedReason(transition?.supportsCheckByFailedReason);
  }, [transitionEnums, transitionType, modalOpen]);

  function handleCLose(hasChanges: boolean) {
    onClose(hasChanges);
    setModalOpen(false);
  }

  function getConfig(): CheckByConfiguration {
    if (sampleOutputOnCheckBy === 'notSupported') {
      if (supportsCheckByFailedReason) {
        return OutputsNotSupportedAndSupportsCheckByIssueConfiguration;
      }

      return OutputsNotSupportConfiguration;
    }

    if (isSequencing) {
      return SequencingConfiguration;
    }

    if (supportsCheckByFailedReason) {
      return DefaultSupportsCheckByIssueConfiguration;
    }

    return DefaultConfiguration;
  }

  function getModal() {
    if (!transitionEnum) {
      return <></>;
    }

    switch (sampleIdResolverApproach) {
      case 'normal':
        return (
          <NormalResolverCheckByModal
            {...{ researchProjectId, configuredTransitionId }}
            onClose={handleCLose}
            transitionEnum={transitionEnum}
            config={getConfig()}
          />
        );

      case 'historic':
        return (
          <HistoricResolverCheckByModal
            {...{ researchProjectId, configuredTransitionId }}
            onClose={handleCLose}
            transitionEnum={transitionEnum}
            config={getConfig()}
          />
        );
    }
  }

  return (
    <>
      <DialogOpenButton title={t(recordQc)} onClick={() => setModalOpen(true)} />
      {modalOpen && getModal()}
    </>
  );
};

const NormalResolverCheckByModal = ({
  config,
  transitionEnum,
  onClose,
  researchProjectId,
  configuredTransitionId,
}: {
  config: CheckByConfiguration;
  transitionEnum: TransitionEnum;
  onClose: OnCloseFunction;
  researchProjectId: string;
  configuredTransitionId: string;
}) => {
  type State = 'TextGrid' | 'ParsedGrid';
  const [state, setState] = useState<State>('TextGrid');
  const [loadingState, setLoadingState] = useState<LoadingState>({ status: 'NotStarted' });

  const { accessToken } = useAuth();

  const [parentExhaustion, setParentExhaustion] = useState<ParentExhaustion>('none');
  const [supportsSampleExhaustion, setSupportsSampleExhaustion] = useState<boolean>(true);
  const checkByFailedReasons = useCheckByFailedReasons(transitionEnum.transitionEnumId);

  const [stringData, setStringData] = useState<string>('');
  const [parsedData, setParsedData] = useState<SampleTrackingIdMapping[]>([]);
  const [supplementalFiles, setSupplementalFiles] = useState<SupplementalFile[]>([]);

  useEffect(() => {
    setParentExhaustion(transitionEnum?.defaultExhaustionApproach ?? 'none');
    setSupportsSampleExhaustion(transitionEnum?.supportsSampleExhaustion);
  }, [transitionEnum]);

  function handleCLose(hasChanges: boolean) {
    onClose(hasChanges);
    setState('TextGrid');
    setParsedData([]);
    setSupplementalFiles([]);
    setLoadingState({ status: 'NotStarted' });
  }

  function createQualityCheckWrapped(dataToSave: SampleTrackingIdMapping[]) {
    ErrorManagement('Loading', setLoadingState, () => createQualityCheck(dataToSave));
  }

  async function createQualityCheck(dataToSave: SampleTrackingIdMapping[]) {
    if (!accessToken) {
      return;
    }

    const idMappingData = await CheckByValidation(
      researchProjectId,
      configuredTransitionId,
      dataToSave.map(i => {
        return {
          sampleIdentifier: i.sampleIdentifier,
          labAssignedSampleId: i.labAssignedSampleId,
          qualityCheckStatus: i.qualityCheckStatus,
        };
      }),
      accessToken
    );

    // Check if anything didn't map
    const invalidData = idMappingData.filter(i => !i.mappedToSampleId);

    if (invalidData.length > 0) {
      setLoadingState({
        status: 'Error',
        errorMessage: invalidData.map(i => `'${i.sampleIdentifier}' did not resolve to any sample.`).join('\n'),
      });
      return;
    }

    const data = idMappingData.map(i => {
      const original = find(
        dataToSave,
        p => p.sampleIdentifier === i.sampleIdentifier && p.labAssignedSampleId === i.labAssignedSampleId
      );

      const toReturn: SampleTrackingCheckBy = {
        sampleIdentifier: i.sampleIdentifier,
        labAssignedSampleId: i.labAssignedSampleId,
        originalSampleId: i.mappedToSampleId ?? '',
        qualityCheckStatus: i.qualityCheckStatus,
        checkByFailedReason: original?.checkByFailedReason,
        r1FastQLocation: original?.r1FastQLocation,
        r2FastQLocation: original?.r2FastQLocation,
      };

      return toReturn;
    });

    const qualityCheckGroupId = await CreateQualityCheck(
      researchProjectId,
      configuredTransitionId,
      data,
      parentExhaustion,
      accessToken
    );

    for (const file of supplementalFiles) {
      await CreateSupplementalFilesForQualityCheckGroupId(qualityCheckGroupId, file, accessToken);
    }

    await onClose(true);
  }

  function getModal() {
    switch (state) {
      case 'TextGrid':
        return (
          <TextGridModal
            {...{ config, transitionEnum, checkByFailedReasons }}
            onClose={handleCLose}
            loadingProps={[loadingState, setLoadingState]}
            stringDataProps={[stringData, setStringData]}
            setParsedData={data => {
              setParsedData(data);
              setState('ParsedGrid');
            }}
          />
        );

      case 'ParsedGrid':
        return (
          <ParsedGridModal
            {...{ config, transitionEnum, checkByFailedReasons }}
            onClose={handleCLose}
            loadingProps={[loadingState, setLoadingState]}
            supplementalFileProps={[supplementalFiles, setSupplementalFiles]}
            parentExhaustionProps={[parentExhaustion, setParentExhaustion]}
            supportsSampleExhaustion={supportsSampleExhaustion}
            data={parsedData}
            onSubmit={createQualityCheckWrapped}
            handleBackToOriginal={() => {
              setState('TextGrid');
              setParsedData([]);
              setLoadingState({ status: 'NotStarted' });
            }}
          />
        );
    }
  }

  return <>{<BaseModal maxWidth={'lg'}>{getModal()}</BaseModal>}</>;
};

const HistoricResolverCheckByModal = ({
  config,
  transitionEnum,
  onClose,
  researchProjectId,
  configuredTransitionId,
}: {
  config: CheckByConfiguration;
  transitionEnum: TransitionEnum;
  onClose: OnCloseFunction;
  researchProjectId: string;
  configuredTransitionId: string;
}) => {
  type State = 'TextGrid' | 'ParsedGrid' | 'Validation';
  const { accessToken } = useAuth();

  const [state, setState] = useState<State>('TextGrid');
  const [loadingState, setLoadingState] = useState<LoadingState>({ status: 'NotStarted' });

  const [parentExhaustion, setParentExhaustion] = useState<ParentExhaustion>('none');
  const [supportsSampleExhaustion, setSupportsSampleExhaustion] = useState<boolean>(true);
  const checkByFailedReasons = useCheckByFailedReasons(transitionEnum.transitionEnumId);

  const [stringData, setStringData] = useState<string>('');
  const [parsedData, setParsedData] = useState<SampleTrackingIdMapping[]>([]);
  const [validationResult, setValidationResult] = useState<ReadonlyArray<SampleTrackingCheckByValidationResult>>([]);
  const [fastQValidationResult, setFastQValidationResult] = useState<
    Dictionary<SampleTrackingCheckByFastqValidationResultPayload>
  >({});
  const [supplementalFiles, setSupplementalFiles] = useState<SupplementalFile[]>([]);

  useEffect(() => {
    setParentExhaustion(transitionEnum?.defaultExhaustionApproach ?? 'none');
    setSupportsSampleExhaustion(transitionEnum?.supportsSampleExhaustion ?? false);
  }, [transitionEnum]);

  function handleCLose(hasChanges: boolean) {
    onClose(hasChanges);
    setState('TextGrid');
    setParsedData([]);
    setSupplementalFiles([]);
    setLoadingState({ status: 'NotStarted' });
  }

  function getCheckByValidationWrapped(
    dataToSave: SampleTrackingIdMapping[],
    setHasValidatedDataErrors: UseStateSetter<boolean>
  ) {
    ErrorManagement('Loading', setLoadingState, () => getCheckByValidation(dataToSave, setHasValidatedDataErrors));
  }

  async function getCheckByValidation(
    dataToSave: SampleTrackingIdMapping[],
    setHasValidatedDataErrors: UseStateSetter<boolean>
  ) {
    const result = await CheckByValidation(
      researchProjectId,
      configuredTransitionId,
      dataToSave.map(i => {
        return {
          sampleIdentifier: i.sampleIdentifier,
          labAssignedSampleId: i.labAssignedSampleId,
          qualityCheckStatus: i.qualityCheckStatus,
        };
      }),
      accessToken
    );
    const validationDataResult = map([...result], i => {
      return { ...i, errorMessage: i.mappedToSampleId ? undefined : 'Did not map to any sample' };
    });

    const hasErrors = validationDataResult.some(r => (r.errorMessage?.length ?? 0) > 0);
    setHasValidatedDataErrors(hasErrors);
    setValidationResult(validationDataResult);

    const toValidate = result
      .map(i => {
        const original = find(
          dataToSave,
          p => p.sampleIdentifier === i.sampleIdentifier && p.labAssignedSampleId === i.labAssignedSampleId
        );

        return {
          sampleIdentifier: i.sampleIdentifier,
          labAssignedSampleId: i.labAssignedSampleId,
          transitionId: i.mappedToSampleCurrentTransition?.transitionId ?? '',
          r1FastQLocation: original?.r1FastQLocation ?? '',
          r2FastQLocation: original?.r2FastQLocation ?? '',
        };
      })
      .filter(i => i.transitionId);

    const fastQResult = toValidate.length !== 0 ? await CheckByFastQValidation(toValidate, accessToken) : [];
    setFastQValidationResult(keyBy(fastQResult, i => i.validated[0].transitionId));

    setLoadingState({ status: 'Complete' });
    setState('Validation');
  }

  function createQualityCheckWrapped(dataToSave: SampleTrackingCheckBy[]) {
    ErrorManagement('Loading', setLoadingState, () => createQualityCheck(dataToSave));
  }

  async function createQualityCheck(dataToSave: SampleTrackingCheckBy[]) {
    const qualityCheckGroupId = await CreateQualityCheck(
      researchProjectId,
      configuredTransitionId,
      dataToSave,
      parentExhaustion,
      accessToken
    );

    for (const file of supplementalFiles) {
      await CreateSupplementalFilesForQualityCheckGroupId(qualityCheckGroupId, file, accessToken);
    }

    await onClose(true);
  }

  function getModal() {
    switch (state) {
      case 'TextGrid':
        return (
          <TextGridModal
            {...{ config, transitionEnum, checkByFailedReasons }}
            onClose={handleCLose}
            loadingProps={[loadingState, setLoadingState]}
            stringDataProps={[stringData, setStringData]}
            setParsedData={async data => {
              setParsedData(data);

              const hasErrors = data.some(r => (r.errorMessage?.length ?? 0) > 0);

              if (!hasErrors) {
                getCheckByValidationWrapped(data, () => {});
              } else {
                setState('ParsedGrid');
              }
            }}
          />
        );

      case 'ParsedGrid':
        return (
          <ParsedGridModal
            {...{ config, transitionEnum, checkByFailedReasons }}
            onClose={handleCLose}
            loadingProps={[loadingState, setLoadingState]}
            supplementalFileProps={[supplementalFiles, setSupplementalFiles]}
            parentExhaustionProps={[parentExhaustion, setParentExhaustion]}
            supportsSampleExhaustion={supportsSampleExhaustion}
            data={parsedData}
            onSubmit={getCheckByValidationWrapped}
            handleBackToOriginal={() => {
              setState('TextGrid');
              setParsedData([]);
            }}
            submitBtnTextKey={'validateMappings'}
          />
        );

      case 'Validation':
        return (
          <ValidationGridModal
            {...{ transitionEnum }}
            onClose={handleCLose}
            loadingProps={[loadingState, setLoadingState]}
            supplementalFileProps={[supplementalFiles, setSupplementalFiles]}
            parentExhaustionProps={[parentExhaustion, setParentExhaustion]}
            supportsSampleExhaustion={supportsSampleExhaustion}
            data={validationResult}
            fastQValidationData={fastQValidationResult}
            onSubmit={createQualityCheckWrapped}
            handleBackToOriginal={() => {
              setState('TextGrid');
              setParsedData([]);
              setLoadingState({ status: 'NotStarted' });
            }}
          />
        );
    }
  }

  return <>{<BaseModal maxWidth={'xl'}>{getModal()}</BaseModal>}</>;
};

const TextGridModal = ({
  config,
  transitionEnum,
  onClose,
  setParsedData,
  loadingProps,
  stringDataProps,
  checkByFailedReasons,
}: {
  config: CheckByConfiguration;
  transitionEnum: TransitionEnum;
  onClose: OnCloseFunction;
  setParsedData: (data: SampleTrackingIdMapping[]) => void;
  loadingProps: LoadingProps;
  stringDataProps: UseState<string>;
  checkByFailedReasons: ReadonlyArray<CheckByFailedReason>;
}) => {
  const { t } = UseMemoTranslation();
  const isSequencing = transitionEnum.name === 'Sequencing';

  const [loadingState] = loadingProps;
  const [stringData, setStringData] = stringDataProps;

  function processData() {
    let index = 1;
    let maxParsedValues = config.maxParsedValues;

    const data: SampleTrackingIdMapping[] = [];

    stringData.split('\n').forEach(rowString => {
      if (!rowString || rowString.length === 0) {
        return;
      }

      const row = rowString.split(/[\t,]/);
      let warningMessages: IdMappingErrorMessageType[] = [];

      if (row.length > maxParsedValues) {
        warningMessages.push(!isSequencing ? 'tooManyParsedItemsDefault' : 'tooManyParsedItemsSequencing');
      }

      const parsedRow = config.rowBuilder(row, index++);

      parsedRow.warningMessage = warningMessages;
      parsedRow.errorMessage = getRowErrorMessages(parsedRow, config, checkByFailedReasons);

      data.push(parsedRow);
    });

    setParsedData([...data]);
  }

  return (
    <>
      <BaseHeader {...{ transitionEnum, onClose }} />
      <DialogContent>
        <Box sx={{ marginTop: 1 }}>
          <TextField
            multiline
            fullWidth
            label='Mapping'
            type='text'
            onChange={event => setStringData(event.target.value)}
            helperText={t(config.inputTextKey as any)}
            autoFocus
            rows={15}
            variant='outlined'
            value={stringData}
            spellCheck={false}
          />
        </Box>
      </DialogContent>
      <BaseActions {...{ onClose, loadingState }}>
        <PrimaryButton onClick={processData} disabled={stringData.length === 0 || loadingState.status === 'Loading'}>
          {t('parse')}
        </PrimaryButton>
      </BaseActions>
    </>
  );
};

const ParsedGridModal = ({
  config,
  transitionEnum,
  onClose,
  data,
  onSubmit,
  loadingProps,
  supplementalFileProps,
  handleBackToOriginal,
  parentExhaustionProps,
  supportsSampleExhaustion,
  submitBtnTextKey = 'submit',
  checkByFailedReasons,
}: {
  config: CheckByConfiguration;
  transitionEnum: TransitionEnum;
  onClose: OnCloseFunction;
  data: SampleTrackingIdMapping[];
  onSubmit: (data: SampleTrackingIdMapping[], setHasValidatedDataErrors: UseStateSetter<boolean>) => void;
  loadingProps: LoadingProps;
  supplementalFileProps: UseState<SupplementalFile[]>;
  handleBackToOriginal: () => void;
  parentExhaustionProps: UseState<ParentExhaustion>;
  supportsSampleExhaustion: boolean;
  submitBtnTextKey?: 'submit' | 'validateMappings';
  checkByFailedReasons: ReadonlyArray<CheckByFailedReason>;
}) => {
  const { t } = UseMemoTranslation();

  const [loadingState] = loadingProps;
  const [dataToSave, setDataToSave] = useState<SampleTrackingIdMapping[]>(data);

  const [hasParsedDataValidationErrors, setHasParsedDataValidationErrors] = useState<boolean>(false);
  const [hasValidatedDataErrors, setHasValidatedDataErrors] = useState<boolean>(false);

  useEffect(() => {
    const hasErrors = dataToSave.some(r => (r.errorMessage?.length ?? 0) > 0);
    setHasParsedDataValidationErrors(hasErrors);
  }, [dataToSave]);

  return (
    <>
      <BaseHeader {...{ transitionEnum, onClose }}>
        <DialogTypography variant='subtitle1'>{t('sampleIdMappingGridInfo')}</DialogTypography>
      </BaseHeader>
      <DialogContent>
        <Box sx={{ marginTop: 1, height: '50vh' }}>
          <SampleTrackingIdMappingGrid
            data={[...dataToSave]}
            onRowUpdate={(rowIndex, newRow) => {
              const index = findIndex(dataToSave, row => row.index === rowIndex);

              if (index !== -1) {
                const row: SampleTrackingIdMapping = {
                  ...dataToSave[index],
                  sampleIdentifier: newRow.sampleIdentifier,
                  labAssignedSampleId: newRow.labAssignedSampleId,
                  qualityCheckStatus: newRow.qualityCheckStatus,
                  checkByFailedReason: newRow.checkByFailedReason,
                  r1FastQLocation: newRow.r1FastQLocation,
                  r2FastQLocation: newRow.r2FastQLocation,
                };

                row.errorMessage = [...getRowErrorMessages(row, config, checkByFailedReasons)];
                dataToSave[index] = row;
              }

              setDataToSave([...dataToSave]);
            }}
            {...{ config, transitionEnum, checkByFailedReasons }}
          />
        </Box>
        {supportsSampleExhaustion && <ExhaustionActions {...{ parentExhaustionProps }} />}
      </DialogContent>
      <BaseActions
        {...{ onClose, loadingState }}
        leftChildren={<SupplementalFilesActions {...{ supplementalFileProps }} />}
      >
        <PrimaryButton onClick={handleBackToOriginal} disabled={loadingState.status === 'Loading'}>
          {t('backToOriginal')}
        </PrimaryButton>
        <PrimaryButton
          onClick={() => onSubmit(dataToSave, setHasValidatedDataErrors)}
          disabled={
            hasParsedDataValidationErrors ||
            hasValidatedDataErrors ||
            dataToSave.length === 0 ||
            loadingState.status === 'Loading'
          }
        >
          {t(submitBtnTextKey)}
        </PrimaryButton>
      </BaseActions>
    </>
  );
};

const ValidationGridModal = ({
  transitionEnum,
  onClose,
  data,
  onSubmit,
  loadingProps,
  supplementalFileProps,
  handleBackToOriginal,
  parentExhaustionProps,
  supportsSampleExhaustion,
  fastQValidationData,
}: {
  transitionEnum: TransitionEnum;
  onClose: OnCloseFunction;
  data: ReadonlyArray<SampleTrackingCheckByValidationResult>;
  onSubmit: (data: SampleTrackingCheckBy[], setHasValidatedDataErrors: UseStateSetter<boolean>) => void;
  loadingProps: LoadingProps;
  supplementalFileProps: UseState<SupplementalFile[]>;
  handleBackToOriginal: () => void;
  parentExhaustionProps: UseState<ParentExhaustion>;
  supportsSampleExhaustion: boolean;
  fastQValidationData: Dictionary<SampleTrackingCheckByFastqValidationResultPayload>;
}) => {
  const { t } = UseMemoTranslation();
  const checkByConfirmationGridApiRef = useGridApiRef();

  const [loadingState] = loadingProps;
  const [fastQDataToSave, setFastQDataToSave] = useState<FastQData[]>([]);
  const [validity, setValidity] = useState<Dictionary<boolean>>({});

  const [hasValidatedDataErrors, setHasValidatedDataErrors] = useState<boolean>(false);

  useEffect(() => {
    const tmp = data.map(i => {
      const original = find(
        values(fastQValidationData).flatMap(i => i.validated),
        j => j.labAssignedSampleId === i.labAssignedSampleId
      );

      return {
        r1FastQLocation: original?.existingSequenceRun?.fastqR1Path ?? original?.r1FastQLocation ?? '',
        r2FastQLocation: original?.existingSequenceRun?.fastqR2Path ?? original?.r2FastQLocation ?? '',
        outputId: i.labAssignedSampleId ?? '',
      };
    });

    setFastQDataToSave(tmp);
  }, [data, fastQValidationData]);

  const onFastQUpdate = useCallback(
    (outputId: string, r1FastQ: string, r2FastQ?: string) => {
      const index = findIndex(fastQDataToSave, row => row.outputId === outputId);

      if (index !== -1) {
        fastQDataToSave[index] = {
          ...fastQDataToSave[index],
          r1FastQLocation: r1FastQ,
          r2FastQLocation: r2FastQ,
        };
      }

      setFastQDataToSave([...fastQDataToSave]);
    },
    [fastQDataToSave]
  );

  return (
    <>
      <BaseHeader {...{ transitionEnum, onClose }} sx={{ pb: 0 }}>
        <DialogTypography variant='subtitle1'>{t('sampleIdMappingGridInfo')}</DialogTypography>
      </BaseHeader>
      <DialogContent>
        <FlexTableBox sx={{ marginTop: 0, height: '50vh' }}>
          <FlexBox flexDirection={'row-reverse'} sx={{ mt: 0 }}>
            <SequenceFilesModal data={values(fastQValidationData)} />
            <GridExportButton apiRef={checkByConfirmationGridApiRef.current} fileName={t('checkByFileName')} />
          </FlexBox>
          <SampleTrackingIdMappingConfirmationGrid
            apiRef={checkByConfirmationGridApiRef}
            transitionEnum={transitionEnum}
            data={data}
            fastQValidationData={fastQValidationData}
            fastQData={fastQDataToSave}
            onFastQUpdate={onFastQUpdate}
            setValidity={(outputId, isValid) => {
              setValidity(prevState => ({ ...prevState, [outputId]: isValid }));
            }}
          />
        </FlexTableBox>
        {supportsSampleExhaustion && <ExhaustionActions {...{ parentExhaustionProps }} />}
      </DialogContent>
      <BaseActions
        {...{ onClose, loadingState }}
        leftChildren={<SupplementalFilesActions {...{ supplementalFileProps }} />}
      >
        <PrimaryButton onClick={handleBackToOriginal} disabled={loadingState.status === 'Loading'}>
          {t('backToOriginal')}
        </PrimaryButton>
        <PrimaryButton
          onClick={() => {
            const outGoing = data.map(i => {
              const original = find(fastQDataToSave, p => p.outputId === i.labAssignedSampleId);

              const toReturn: SampleTrackingCheckBy = {
                sampleIdentifier: i.sampleIdentifier,
                labAssignedSampleId: i.labAssignedSampleId,
                originalSampleId: i.mappedToSampleId ?? '',
                qualityCheckStatus: i.qualityCheckStatus,
                r1FastQLocation: original?.r1FastQLocation,
                r2FastQLocation: original?.r2FastQLocation,
              };

              return toReturn;
            });

            onSubmit(outGoing, setHasValidatedDataErrors);
          }}
          disabled={hasValidatedDataErrors || values(validity).some(i => !i) || loadingState.status === 'Loading'}
        >
          {t('submit')}
        </PrimaryButton>
      </BaseActions>
    </>
  );
};

const SequenceFilesModal = ({ data }: { data: ReadonlyArray<SampleTrackingCheckByFastqValidationResultPayload> }) => {
  const { t } = UseMemoTranslation();

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  return (
    <>
      <DialogOpenButton title={t('sequenceFiles')} onClick={() => setModalOpen(true)}>
        <FullscreenOutlinedIcon />
      </DialogOpenButton>
      <BaseModal maxWidth={'lg'} open={modalOpen}>
        <BaseHeader title={t('sequenceFiles')} onClose={() => setModalOpen(false)} sx={{ pb: 0 }} />
        <DialogContent sx={{ mt: 1 }}>
          <CheckByAvailableSequenceFilesGrid {...{ data }} />
        </DialogContent>
      </BaseModal>
    </>
  );
};

const SupplementalFilesActions = ({
  supplementalFileProps,
}: {
  supplementalFileProps: UseState<SupplementalFile[]>;
}) => {
  const [supplementalFiles, setSupplementalFiles] = supplementalFileProps;

  return (
    <Box>
      <Typography variant={'body1'}>
        <CreateSupplementalFilesModal
          onCreate={supplementalFile => setSupplementalFiles(prevState => [...prevState, supplementalFile])}
        />
        <span>{supplementalFiles.length > 0 && supplementalFiles.length + ' File(s) will be saved'}</span>
      </Typography>
    </Box>
  );
};

const ExhaustionActions = ({ parentExhaustionProps }: { parentExhaustionProps: UseState<ParentExhaustion> }) => {
  const { t } = UseMemoTranslation();

  const [parentExhaustion, setParentExhaustion] = parentExhaustionProps;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', marginTop: 1 }}>
      <Typography variant={'subtitle1'}>{t('exhaustInfoIdMapping')}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'row', marginTop: 0 }}>
        <FormControlLabel
          control={
            <Checkbox
              disabled={parentExhaustion !== 'discard' && parentExhaustion !== 'none'}
              checked={parentExhaustion === 'discard'}
              onChange={() => setParentExhaustion(parentExhaustion === 'discard' ? 'none' : 'discard')}
            />
          }
          label={t('discardOriginal')}
        />
        <FormControlLabel
          control={
            <Checkbox
              disabled={parentExhaustion !== 'exhaust' && parentExhaustion !== 'none'}
              checked={parentExhaustion === 'exhaust'}
              onChange={() => setParentExhaustion(parentExhaustion === 'exhaust' ? 'none' : 'exhaust')}
            />
          }
          label={t('exhaustOriginal')}
        />
        <FormControlLabel
          control={
            <Checkbox
              disabled={parentExhaustion !== 'rebiobank' && parentExhaustion !== 'none'}
              checked={parentExhaustion === 'rebiobank'}
              onChange={() => setParentExhaustion(parentExhaustion === 'rebiobank' ? 'none' : 'rebiobank')}
            />
          }
          label={t('rebiobankOriginal')}
        />
      </Box>
    </Box>
  );
};

const BaseModal = ({
  maxWidth,
  children,
  open = true,
}: {
  maxWidth: WidthSize;
  children: React.ReactNode;
  open?: boolean;
}) => {
  return (
    <Dialog open={open} fullWidth maxWidth={maxWidth}>
      {children}
    </Dialog>
  );
};

const BaseHeader = ({
  transitionEnum,
  onClose,
  children,
  sx,
  title,
}: {
  transitionEnum?: TransitionEnum;
  onClose: OnCloseFunction;
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
  title?: string;
}) => {
  const { t } = UseMemoTranslation();

  function getInfoText() {
    if (!transitionEnum) {
      return '';
    }

    switch (transitionEnum.sampleOutputOnCheckBy) {
      case 'notSupported':
        return t('sampleIdMappingTextFieldInfoNotAlwaysMakeNewSample');
      case 'supportsOutputSamples':
        return t('sampleIdMappingTextFieldInfoNotAlwaysMakeNewSample');
      case 'requiresOutputSamples':
        return t('sampleIdMappingTextFieldInfoAlwaysMakeNewSample');
    }
  }

  return (
    <DialogTitle sx={sx}>
      <DialogCloseButton onClick={() => onClose(false)} />
      {title ?? t('recordQc')}
      {transitionEnum && <DialogTypography variant='subtitle1'>{getInfoText()}</DialogTypography>}
      {children}
    </DialogTitle>
  );
};

const BaseActions = ({
  loadingState,
  onClose,
  children,
  leftChildren,
}: {
  loadingState: LoadingState;
  onClose: OnCloseFunction;
  children?: React.ReactNode;
  leftChildren?: React.ReactNode;
}) => {
  return (
    <>
      <LoadingIndicator loadingState={loadingState} margin={'LR'} />
      <ErrorIndicator loadingState={loadingState} />
      <DialogActions>
        <FlexBox
          sx={{
            alignItems: 'center',
            flexDirection: 'reverse',
            justifyContent: 'space-between',
            width: '100%',
            textAlign: 'center',
          }}
        >
          {leftChildren ?? <Box></Box>}
          <Box>
            <CancelButton onClick={() => onClose(false)} />
            {children}
          </Box>
        </FlexBox>
      </DialogActions>
    </>
  );
};

function getRowErrorMessages(
  row: SampleTrackingIdMapping,
  config: CheckByConfiguration,
  checkByFailedReasons: ReadonlyArray<CheckByFailedReason>
) {
  let errorMessages: IdMappingErrorMessageType[] = [];

  if (!row.sampleIdentifier) {
    errorMessages.push('missingOriginalId');
  }

  if (config.requireLabAssignedId && !row.labAssignedSampleId) {
    errorMessages.push('missingNewLabAssignedId');
  }

  if (!row.qualityCheckStatus) {
    errorMessages.push('missingQualityCheckStatus');
  } else if (!PersistableQualityCheckStatuses.includes(row.qualityCheckStatus as QualityCheckStatus)) {
    errorMessages.push('invalidQualityCheckStatus');
  }

  if (config.supportsCheckByFailedReason && row.checkByFailedReason) {
    const checkByFailedReason = find(
      checkByFailedReasons,
      i => i.name.toLowerCase() === row.checkByFailedReason?.toLowerCase()
    );

    if (!checkByFailedReason) {
      errorMessages.push('invalidCheckByFailedReason');
    }
  }

  return errorMessages;
}
